import { useSelector } from 'react-redux';
import { Box, Chip, DialogContent, Stack, Typography } from '@mui/material';

import {
  ORGANISATION_USER_STATUS,
  ORGANISATION_USER_STATUS_DESCRIPTION,
  ORGANISATION_USER_STATUS_LABEL,
} from 'constants/organisations';
import { CHIP_COLOR_BY_PROJECT_STATUS } from 'constants/commonProjects';
import { dialogPropsSelector } from 'store/entities/ui/slice';
import { InfoTooltip, Label } from 'components/simple';
import { ColoredTable, Dialog } from 'components/complex';

const USER_INFO_CELL_KEYS = {
  USER_STATUS: 'user_status',
  LOGIN_TYPE: 'login_type',
};

const PROJECT_INFO_CELL_KEYS = {
  PROJECT_NAME: 'project_name',
  DATE_ADDED: 'date_added',
  PROJECT_END_DATE: 'project_end_date',
  STATUS: 'status',
  PROJECT_TYPE: 'project_type',
  EMPTY_PROJECTS_LIST: 'empty_projects_list',
};

const userInfoHeaders = [
  {
    cellKey: USER_INFO_CELL_KEYS.USER_STATUS,
    content: 'User Status',
    cellProps: { width: 250 },
  },
  {
    cellKey: USER_INFO_CELL_KEYS.LOGIN_TYPE,
    content: 'Login Type',
  },
];

const projectsInfoHeaders = [
  {
    cellKey: PROJECT_INFO_CELL_KEYS.PROJECT_NAME,
    content: 'User Projects',
  },
  {
    cellKey: PROJECT_INFO_CELL_KEYS.DATE_ADDED,
    content: 'Date Added',
    cellProps: {
      width: 105,
      align: 'center',
    },
  },
  {
    cellKey: PROJECT_INFO_CELL_KEYS.PROJECT_END_DATE,
    content: 'Project End Date',
    cellProps: {
      width: 105,
      align: 'center',
    },
  },
  {
    cellKey: PROJECT_INFO_CELL_KEYS.STATUS,
    content: 'Status',
    cellProps: {
      width: 120,
      align: 'center',
    },
  },
  {
    cellKey: PROJECT_INFO_CELL_KEYS.PROJECT_TYPE,
    content: 'Project Type',
    cellProps: {
      width: 235,
      align: 'center',
    },
  },
];

const formatHeaders = (headers = []) =>
  headers.map(({ content, ...headerData }) => ({
    ...headerData,
    content: (
      <Typography variant="button" color="text.secondary">
        {content}
      </Typography>
    ),
  }));

const styles = {
  dialogTitle: {
    fontWeight: 600,
    padding: 'unset',
  },
  dialogContent: {
    padding: 'unset',
  },
  dialogPaper: {
    maxWidth: '1000px !important',
    padding: 4,
    '& > button': {
      width: 48,
      height: 48,
      top: 32,
      right: 32,
      '& > svg': {
        width: 20,
        height: 20,
      },
    },
  },
  tableWrapper: ({ palette }) => ({
    border: `1px solid ${palette.colors.darkPale}`,
    borderRadius: 5,
    paddingInline: 1,
    maxHeight: 400,
    overflowY: 'auto',
  }),
  userStatusChip: ({ palette }) => ({
    width: 160,
    paddingBlock: 1,
    paddingInline: 3,
    '.MuiChip-label': {
      fontSize: 18,
      color: palette.text.primary,
    },
  }),
  projectStatusChip: ({ palette }) => ({
    width: 105,
    paddingBlock: 0.5,
    paddingInline: 'unset',
    '.MuiChip-label': {
      fontSize: 16,
      fontWeight: 700,
      color: palette.text.primary,
    },
  }),
  tableRow: {
    'th, td': {
      height: 73,
      paddingBlock: 'unset',
      '&:first-of-type': { borderRadius: '20px 0 0 20px' },
      '&:last-of-type': { borderRadius: '0 20px 20px 0' },
    },
  },
  emptyCell: {
    borderRadius: '20px !important',
  },
};

export const OrganisationUserDetailsDialog = () => {
  const { userInfo } = useSelector(dialogPropsSelector);
  const { userName, email, dateAdded, userStatus, loginType, userProjectsList } = userInfo;
  const userInfoRows = [
    {
      id: 'user_info',
      cells: [
        {
          cellKey: USER_INFO_CELL_KEYS.USER_STATUS,
          content: (
            <Stack direction="row" spacing={1}>
              <Chip
                label={ORGANISATION_USER_STATUS_LABEL[userStatus]}
                color={userStatus === ORGANISATION_USER_STATUS.ACTIVE ? 'success' : 'error'}
                sx={styles.userStatusChip}
              />
              <InfoTooltip titleText={ORGANISATION_USER_STATUS_DESCRIPTION[userStatus]} />
            </Stack>
          ),
        },
        {
          cellKey: USER_INFO_CELL_KEYS.LOGIN_TYPE,
          content: (
            <Typography variant="subtitle1" component="p">
              {loginType}
            </Typography>
          ),
        },
      ],
    },
  ];

  const projectsInfoRows = !userProjectsList.length
    ? [
        {
          id: 'project_info',
          cells: [
            {
              cellKey: PROJECT_INFO_CELL_KEYS.EMPTY_PROJECTS_LIST,
              content: (
                <Typography variant="subtitle1-bold" component="span">
                  User doesn’t have any projects yet
                </Typography>
              ),
              cellProps: {
                colSpan: 5,
                align: 'center',
              },
              cellStyles: styles.emptyCell,
            },
          ],
        },
      ]
    : userProjectsList.map(({ id, name, dateAdded: dateAddedToProject, endDate, status, projectType }) => ({
        id,
        cells: [
          {
            cellKey: PROJECT_INFO_CELL_KEYS.PROJECT_NAME,
            content: (
              <Typography variant="body1" noWrap>
                {name}
              </Typography>
            ),
          },
          {
            cellKey: PROJECT_INFO_CELL_KEYS.DATE_ADDED,
            content: (
              <Typography variant="body1" component="span">
                {dateAddedToProject}
              </Typography>
            ),
            cellProps: { align: 'center' },
          },
          {
            cellKey: PROJECT_INFO_CELL_KEYS.PROJECT_END_DATE,
            content: (
              <Typography variant="body1" component="span">
                {endDate}
              </Typography>
            ),
            cellProps: { align: 'center' },
          },
          {
            cellKey: PROJECT_INFO_CELL_KEYS.STATUS,
            content: <Chip label={status} color={CHIP_COLOR_BY_PROJECT_STATUS[status]} sx={styles.projectStatusChip} />,
            cellProps: { align: 'center' },
          },
          {
            cellKey: PROJECT_INFO_CELL_KEYS.PROJECT_TYPE,
            content: <Label labelType={projectType} />,
            cellProps: { align: 'center' },
          },
        ],
      }));

  return (
    <Dialog
      withCustomContent
      id="organisation-user-details"
      title={userName}
      customStyles={{
        titleStyles: styles.dialogTitle,
        paperStyles: styles.dialogPaper,
      }}
    >
      <DialogContent sx={styles.dialogContent}>
        <Stack spacing={4}>
          <Typography variant="subtitle1-bold">{email}</Typography>

          <Typography variant="button" component="p" align="left">
            Date added: {dateAdded}
          </Typography>

          <Box sx={styles.tableWrapper}>
            <ColoredTable
              header={formatHeaders(userInfoHeaders)}
              rows={userInfoRows}
              customStyles={{
                tableRow: styles.tableRow,
                headerRow: styles.tableRow,
              }}
            />
          </Box>

          <Box sx={styles.tableWrapper}>
            <ColoredTable
              header={formatHeaders(projectsInfoHeaders)}
              rows={projectsInfoRows}
              customStyles={{
                tableRow: styles.tableRow,
                headerRow: styles.tableRow,
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
